// @ts-nocheck: converted from JS

import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@/components/UI/Button';
import { Panel, Tab, Tabs } from '@/components/UI/Tabs';
import { FormProvider, useForm } from 'react-hook-form@latest';
import Header from '@/components/UI/Header';
import ApiErrorBanner from '@/components/ApiErrorBanner';
import { useRequest } from '@/support/Hooks/request';
import { getProviderComponent, providerNameMap, ssoProviders } from './SingleSignOn.utils';
import { successToast } from '@/helpers';
import { useSelector } from 'react-redux';
import type { State } from '@/model/State.model';
import type { SsoProvider } from './SingleSignOn.types';
import { RouteService } from '@/support/RouteService';

export interface FormValues {
  azure: {
    enabled: boolean;
    tenant: string;
    client_id: string;
    client_secret: string;
  };
  okta: {
    enabled: boolean;
    domain: string;
    client_id: string;
    client_secret: string;
  };
  saml2: {
    enabled: boolean;
    audience: string;
    issuer: string;
  };
}

const SingleSignOn = () => {
  const providerState = useState(0);
  const [provider, setProvider] = providerState;

  const { availableProviders, activeProvider } = useMemo(() => {
    return {
      availableProviders: ssoProviders,
      activeProvider: ssoProviders[provider],
    };
  }, [provider]);

  useEffect(() => {
    if (provider >= availableProviders.length) {
      setProvider(0);
    }
  }, [availableProviders.length, provider, setProvider]);

  const [hasSaml2MetadataFile, setHasSaml2MetadataFile] = useState(false);

  const [providerLoading, setProviderLoading] = useState<Record<SsoProvider, boolean>>({
    azure: true,
    okta: true,
    saml2: true,
  });

  const handleProviderLoadingChange = useCallback((provider: SsoProvider, loading: boolean) => {
    setProviderLoading((prev) => ({ ...prev, [provider]: loading }));
  }, []);

  const handleSaml2MetadataFileChange = useCallback((hasFile: boolean) => {
    setHasSaml2MetadataFile(hasFile);
  }, []);

  // Memoize provider props to prevent unnecessary re-renders
  const providerProps = useMemo(
    () => ({
      azure: { onLoadingChange: (loading: boolean) => handleProviderLoadingChange('azure', loading) },
      okta: { onLoadingChange: (loading: boolean) => handleProviderLoadingChange('okta', loading) },
      saml2: {
        onMetadataFileChange: handleSaml2MetadataFileChange,
        onLoadingChange: (loading: boolean) => handleProviderLoadingChange('saml2', loading),
      },
    }),
    [handleProviderLoadingChange, handleSaml2MetadataFileChange]
  );

  const useFormMethods = useForm<FormValues>({
    defaultValues: {
      azure: { enabled: false, tenant: '', client_id: '', client_secret: '' },
      okta: { enabled: false, domain: '', client_id: '', client_secret: '' },
      saml2: { enabled: false, audience: '', issuer: '' },
    },
  });
  const { handleSubmit } = useFormMethods;

  const {
    sendRequest: updateSSOProvider,
    requestSending: updatingSSOProvider,
    requestError,
  } = useRequest({
    route: 'team.sso_providers',
    params: { type: activeProvider },
    method: 'put',
  });

  const onSubmit = async (data) => {
    const providerFormData = data[activeProvider] ?? {};
    const providerName = providerNameMap[activeProvider];
    await updateSSOProvider(providerFormData);
    if (!requestError) {
      successToast(`Updated SSO settings for ${providerName}`);
    }
  };

  return (
    <>
      <div>
        <Header mainHeaderText="Setup single sign on (SSO)" />
        <p className="lh-copy f6">
          Enter your selected provider details to enable SSO.{' '}
          <a href={RouteService.help('single_sign_on')} target="_blank" rel="noopener noreferrer" className="link blue">
            How do I setup my team SSO?
          </a>
        </p>
      </div>
      <div className="ui-card mw7 mv4">
        {requestError && <ApiErrorBanner className="mb4" error={requestError} />}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Tabs outerState={providerState}>
            <div className="ph3 pt3 mb3 bb b--moon-gray">
              {availableProviders.map((provider, index) => (
                <Tab key={index} className="f5">
                  {providerNameMap[provider]}
                </Tab>
              ))}
            </div>
            {availableProviders.map((provider, index) => (
              <Panel key={index}>
                <FormProvider {...useFormMethods}>
                  {getProviderComponent(provider, providerProps[provider])}
                </FormProvider>
              </Panel>
            ))}
          </Tabs>
          <div className="pa3">
            <Button
              submit
              disabled={
                updatingSSOProvider ||
                providerLoading[activeProvider] ||
                (activeProvider === 'saml2' && !hasSaml2MetadataFile)
              }
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default SingleSignOn;
